<script setup lang="ts" generic="T extends string">
import {
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';

import Tooltip from './Tooltip.vue';

export interface Tab<T> {
  badge?: null | number | string;
  disabled?: boolean;
  id: T;
  label: string;
  tooltip?: null | string;
}

interface Emits {
  (_e: 'update:modelValue', _value: T): void;
}

interface Props {
  modelValue: string;
  tabs: Tab<T>[];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const activeTab = ref(props.modelValue);
const tabsNavRef = ref<HTMLElement | null>(null);
const sliderRef = ref<HTMLElement | null>(null);

const updateSliderPosition = () => {
  if (!tabsNavRef.value || !sliderRef.value) return;

  nextTick(() => {
    if (!tabsNavRef.value || !sliderRef.value) return;

    const activeButton = tabsNavRef.value.querySelector(`[data-tab-id="${activeTab.value}"]`) as HTMLElement;
    if (!activeButton) return;

    const navRect = tabsNavRef.value.getBoundingClientRect();
    const { left: buttonLeft, width: buttonWidth } = activeButton.getBoundingClientRect();

    const left = buttonLeft - navRect.left;

    tabsNavRef.value.style.setProperty('--slider-left', `${left}px`);
    tabsNavRef.value.style.setProperty('--slider-width', `${buttonWidth}px`);
  });
};

watch(
  () => props.modelValue,
  (newValue) => {
    activeTab.value = newValue;
    nextTick(() => {
      updateSliderPosition();
    });
  },
);

onMounted(() => {
  updateSliderPosition();
  window.addEventListener('resize', updateSliderPosition);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSliderPosition);
});

const handleTabClick = (tabId: T, disabled: boolean | undefined) => {
  if (disabled) return;

  activeTab.value = tabId;
  emit('update:modelValue', tabId);

  nextTick(() => {
    updateSliderPosition();
  });
};
</script>

<template>
  <div class="tabs-component">
    <div ref="tabsNavRef" class="tabs-nav">
      <div ref="sliderRef" class="tab-slider" />
      <template v-for="tab in tabs" :key="tab.id">
        <Tooltip
          :content="tab.tooltip || ''"
          placement="top"
          :disabled="!tab.disabled || !tab.tooltip"
        >
          <button
            :class="['tab-button', { active: activeTab === tab.id, disabled: tab.disabled }]"
            :data-tab-id="tab.id"
            type="button"
            :disabled="tab.disabled"
            @click="handleTabClick(tab.id, tab.disabled)"
          >
            <span class="tab-label">{{ tab.label }}</span>
            <span v-if="tab.badge != null" class="tab-badge">{{ tab.badge }}</span>
          </button>
        </Tooltip>
      </template>
    </div>

    <div class="tabs-content">
      <slot :active-tab="activeTab" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.tabs-component {
  width: 100%;
}

.tabs-nav {
  --slider-left: 0px;
  --slider-width: 0px;

  display: flex;
  background: #e5e7eb;
  padding: 0.375rem;
  border-radius: 0.625rem;
  gap: 0.375rem;
  position: relative;
  box-sizing: border-box;
}

.tab-slider {
  position: absolute;
  top: 0.375rem;
  bottom: 0.375rem;
  left: var(--slider-left);
  width: var(--slider-width);
  background: linear-gradient(135deg, #00B569 0%, #009955 100%);
  border-radius: 0.5rem;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
  box-shadow:
    0 4px 12px rgba(0, 181, 105, 0.25),
    0 2px 4px rgba(0, 181, 105, 0.15);
  pointer-events: none;
}

.tab-button {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  font-size: 0.9375rem;
  border-radius: 0.5rem;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  z-index: 1;
  transition: color 0.2s ease, background-color 0.2s ease;
  min-width: 0;
  box-sizing: border-box;
  flex-shrink: 1;
  flex-grow: 1;
  flex-basis: 0;

  &:hover:not(.disabled):not(.active) {
    background: rgba(0, 181, 105, 0.15);
    color: #00B569;
  }

  &.active {
    color: white;

    .tab-badge {
      background: rgba(255, 255, 255, 0.25);
      color: white;
      border-color: rgba(255, 255, 255, 0.4);
    }
  }

  &.disabled {
    color: #cbd5e1;
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background: transparent;
    }

    .tab-badge {
      opacity: 0.7;
    }
  }

  .tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6875rem;
    font-weight: 700;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    background: #e2e8f0;
    color: #64748b;
    border: 1px solid #cbd5e1;
    min-width: 1.5rem;
    transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease;
    flex-shrink: 0;
  }
}

.tabs-content {
  margin-top: 1.5rem;
}
</style>
